import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Upload,
  Link as LinkIcon,
  X,
  Play,
  Pause,
  Maximize2,
  Volume2,
  VolumeX,
  FileVideo,
} from "lucide-react";
import SourceDialog from "./SourceDialog";
import DrawingCanvas from "./DrawingCanvas";
import DrawingToolbar from "./DrawingToolbar";
import { formatTime, detectYouTubeId } from "../lib/videoUtils";
import { toast } from "sonner";

const VideoPanel = forwardRef(function VideoPanel(
  { label, source, setSource, syncEnabled, globalRate, layout, onPlayChange, onSeek },
  ref
) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const ytPlayerRef = useRef(null);   // YT.Player instance for YouTube
  const ytReadyRef = useRef(false);   // true once the player fires onReady
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlayingLocal, setIsPlayingLocal] = useState(false);
  const [muted, setMuted] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Drawing state
  const [tool, setTool] = useState("none"); // 'none' | 'pen' | 'line' | 'arrow' | 'circle'
  const [color, setColor] = useState("#E4FF00");
  const [strokes, setStrokes] = useState([]);

  const isYouTube = source?.type === "youtube";
  const youTubeId = useMemo(
    () => (isYouTube ? detectYouTubeId(source.url) : null),
    [isYouTube, source]
  );

  // Initialize / destroy YouTube IFrame Player when source changes
  useEffect(() => {
    if (!isYouTube || !youTubeId) {
      // Destroy any existing YT player when switching away from YouTube
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch (e) {}
        ytPlayerRef.current = null;
        ytReadyRef.current = false;
      }
      return;
    }

    const containerId = `yt-player-${label}`;

    const createPlayer = () => {
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch (e) {}
        ytPlayerRef.current = null;
        ytReadyRef.current = false;
      }
      ytPlayerRef.current = new window.YT.Player(containerId, {
        videoId: youTubeId,
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: () => { ytReadyRef.current = true; },
          onStateChange: (e) => {
            const S = window.YT.PlayerState;
            if (e.data === S.PLAYING) {
              setIsPlayingLocal(true);
              onPlayChange?.(true);
            } else if (e.data === S.PAUSED || e.data === S.ENDED) {
              setIsPlayingLocal(false);
              onPlayChange?.(false);
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      // Load the IFrame API script once
      if (!document.getElementById('yt-iframe-api')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
      // Queue until API is ready
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        createPlayer();
      };
    }

    return () => {
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch (e) {}
        ytPlayerRef.current = null;
        ytReadyRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYouTube, youTubeId]);

  // Expose imperative methods to parent
  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        if (isYouTube) {
          if (ytReadyRef.current && ytPlayerRef.current) {
            ytPlayerRef.current.playVideo();
          }
          return Promise.resolve();
        }
        const v = videoRef.current;
        if (v) return v.play().catch(() => {});
        return Promise.resolve();
      },
      pause: () => {
        if (isYouTube) {
          if (ytReadyRef.current && ytPlayerRef.current) {
            ytPlayerRef.current.pauseVideo();
          }
          return;
        }
        const v = videoRef.current;
        if (v) v.pause();
      },
      isPlaying: () => {
        if (isYouTube) {
          if (!ytReadyRef.current || !ytPlayerRef.current) return false;
          return ytPlayerRef.current.getPlayerState() === window.YT?.PlayerState?.PLAYING;
        }
        const v = videoRef.current;
        return v ? !v.paused : false;
      },
      seekTo: (time) => {
        if (isYouTube) {
          if (ytReadyRef.current && ytPlayerRef.current) {
            ytPlayerRef.current.seekTo(time, true);
          }
          return;
        }
        const v = videoRef.current;
        if (v) v.currentTime = Math.max(0, Math.min(v.duration || 0, time));
      },
      setRate: (r) => {
        if (isYouTube) {
          if (ytReadyRef.current && ytPlayerRef.current) {
            ytPlayerRef.current.setPlaybackRate(r);
          }
          return;
        }
        const v = videoRef.current;
        if (v) v.playbackRate = r;
      },
      stepFrame: (delta) => {
        const v = videoRef.current;
        if (!v) return;
        v.pause();
        v.currentTime = Math.max(
          0,
          Math.min(v.duration || 0, v.currentTime + delta)
        );
      },
      waitForCanPlay: () => {
        if (isYouTube) {
          // Resolve once YT player is ready
          if (ytReadyRef.current) return Promise.resolve();
          return new Promise((resolve) => {
            const check = setInterval(() => {
              if (ytReadyRef.current) { clearInterval(check); resolve(); }
            }, 100);
            setTimeout(() => { clearInterval(check); resolve(); }, 5000);
          });
        }
        const v = videoRef.current;
        if (!v) return Promise.resolve();
        if (v.readyState >= 3) return Promise.resolve();
        return new Promise((resolve) => {
          const done = () => {
            v.removeEventListener('canplay', done);
            v.removeEventListener('canplaythrough', done);
            v.removeEventListener('error', done);
            resolve();
          };
          v.addEventListener('canplay', done);
          v.addEventListener('canplaythrough', done);
          v.addEventListener('error', done);
          setTimeout(done, 3000);
        });
      },
      getVideoElement: () => isYouTube ? null : videoRef.current,
      getVideoFile: () => (!isYouTube && source?.file) ? source.file : null,
      getCurrentTime: () => {
        if (isYouTube) return ytPlayerRef.current?.getCurrentTime?.() ?? 0;
        return videoRef.current?.currentTime ?? 0;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isYouTube, source?.file]
  );

  // Apply rate when synced
  useEffect(() => {
    const v = videoRef.current;
    if (v && syncEnabled) v.playbackRate = globalRate;
  }, [globalRate, syncEnabled, source]);

  // Clean up object URLs
  useEffect(() => {
    return () => {
      if (source?.type === "file" && source.url?.startsWith("blob:")) {
        URL.revokeObjectURL(source.url);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.url]);

  const handlePlayPauseToggle = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  }, []);

  const onLoadedMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
    v.playbackRate = syncEnabled ? globalRate : 1;
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
  };

  const onPlay = () => {
    setIsPlayingLocal(true);
    onPlayChange?.(true);
  };

  const onPause = () => {
    setIsPlayingLocal(false);
    onPlayChange?.(false);
  };

  const onScrubberChange = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const t = parseFloat(e.target.value);
    v.currentTime = t;
    setCurrentTime(t);
    onSeek?.(t);
  };

  const clearSource = () => {
    if (source?.type === "file" && source.url?.startsWith("blob:")) {
      URL.revokeObjectURL(source.url);
    }
    setSource(null);
    setStrokes([]);
    setDuration(0);
    setCurrentTime(0);
    toast(`Panel ${label} cleared`);
  };

  const clearStrokes = () => setStrokes([]);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen?.();
    }
  };

  const hasSource = !!source?.url;

  return (
    <div
      ref={containerRef}
      className="relative bg-black border border-transparent overflow-hidden flex flex-col min-h-0 group"
      data-testid={`video-panel-${label}`}
    >
      {/* Label badge */}
      <div className="absolute top-3 left-3 z-30 flex items-center gap-2">
        <span
          className="font-display text-2xl md:text-3xl uppercase leading-none text-white bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-md border border-white/10"
          data-testid={`panel-label-${label}`}
        >
          {label}
        </span>
        {source?.name && (
          <span className="text-xs font-mono text-white/70 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md border border-white/10 max-w-[180px] md:max-w-[260px] truncate">
            {source.name}
          </span>
        )}
      </div>

      {/* Top-right actions */}
      {hasSource && (
        <div className="absolute top-3 right-3 z-30 flex items-center gap-1.5">
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-md bg-black/60 backdrop-blur-sm border border-white/10 text-white/70 hover:text-white hover:bg-black/80 transition-colors"
            data-testid={`fullscreen-${label}-button`}
            title="Fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            onClick={clearSource}
            className="p-2 rounded-md bg-black/60 backdrop-blur-sm border border-white/10 text-white/70 hover:text-[#FF3B30] hover:bg-black/80 transition-colors"
            data-testid={`clear-source-${label}-button`}
            title="Remove video"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Video area */}
      <div className="relative flex-1 min-h-0 flex items-center justify-center bg-black">
        {!hasSource && (
          <EmptyState
            label={label}
            onPickFile={() => setDialogOpen(true)}
            onPickUrl={() => setDialogOpen(true)}
          />
        )}

        {hasSource && !isYouTube && (
          <video
            ref={videoRef}
            src={source.url}
            className="max-h-full max-w-full w-full h-full object-contain"
            onLoadedMetadata={onLoadedMeta}
            onTimeUpdate={onTimeUpdate}
            onPlay={onPlay}
            onPause={onPause}
            onClick={handlePlayPauseToggle}
            onError={() =>
              toast.error(
                `Could not load video ${label}. Check the URL or try a local file.`
              )
            }
            playsInline
            muted={muted}
            crossOrigin="anonymous"
            data-testid={`video-element-${label}`}
          />
        )}

        {hasSource && isYouTube && youTubeId && (
          <div
            id={`yt-player-${label}`}
            className="w-full h-full"
            data-testid={`youtube-iframe-${label}`}
          />
        )}

        {/* Drawing canvas (only for direct video) */}
        {hasSource && !isYouTube && tool !== "none" && (
          <DrawingCanvas
            strokes={strokes}
            setStrokes={setStrokes}
            tool={tool}
            color={color}
            data-testid={`drawing-canvas-${label}`}
          />
        )}
        {hasSource && !isYouTube && tool === "none" && strokes.length > 0 && (
          <DrawingCanvas
            strokes={strokes}
            setStrokes={setStrokes}
            tool="none"
            color={color}
            readOnly
          />
        )}

        {/* YouTube notice */}
        {isYouTube && (
          <div className="absolute bottom-3 left-3 z-20 bg-black/70 backdrop-blur-sm border border-[#E4FF00]/30 text-[#E4FF00] text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1.5 rounded-md">
            YouTube · Limited controls
          </div>
        )}
      </div>

      {/* Bottom scrubber + controls (only for direct video).
          In vertical layout this renders as a hover-revealed overlay so the gap stays at 1px. */}
      {hasSource && !isYouTube && (
        <div
          className={
            layout === "vertical"
              ? "absolute left-0 right-0 bottom-0 z-30 bg-black/70 backdrop-blur-md border-t border-white/10 px-3 py-2 flex items-center gap-3 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200"
              : "bg-[#0F0F0F] border-t border-white/10 px-3 py-2.5 flex items-center gap-3"
          }
          data-testid={`scrubber-row-${label}`}
        >
          <button
            onClick={handlePlayPauseToggle}
            className="w-9 h-9 rounded-md bg-white/[0.05] hover:bg-white/10 flex items-center justify-center text-white transition-colors shrink-0"
            data-testid={`local-play-${label}-button`}
          >
            {isPlayingLocal ? (
              <Pause className="w-4 h-4 fill-white" />
            ) : (
              <Play className="w-4 h-4 fill-white ml-0.5" />
            )}
          </button>

          <span className="text-[11px] font-mono text-white/60 tabular-nums shrink-0 w-16 text-right">
            {formatTime(currentTime)}
          </span>

          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.01"
            value={currentTime}
            onChange={onScrubberChange}
            className="track-slider flex-1"
            data-testid={`scrubber-${label}`}
          />

          <span className="text-[11px] font-mono text-white/60 tabular-nums shrink-0 w-16">
            {formatTime(duration)}
          </span>

          <button
            onClick={() => setMuted((m) => !m)}
            className="w-9 h-9 rounded-md hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors shrink-0"
            data-testid={`mute-${label}-button`}
          >
            {muted ? (
              <VolumeX className="w-4 h-4" />
            ) : (
              <Volume2 className="w-4 h-4" />
            )}
          </button>
        </div>
      )}

      {/* Drawing toolbar (bottom-center, floating) */}
      {hasSource && !isYouTube && (
        <DrawingToolbar
          tool={tool}
          setTool={setTool}
          color={color}
          setColor={setColor}
          onClear={clearStrokes}
          hasStrokes={strokes.length > 0}
          label={label}
          layout={layout}
        />
      )}

      <SourceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSelect={(src) => setSource(src)}
        label={label}
      />
    </div>
  );
});

function EmptyState({ label, onPickFile }) {
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-grid"
      data-testid={`empty-state-${label}`}
    >
      <div className="w-16 h-16 rounded-xl border border-white/15 bg-white/[0.03] flex items-center justify-center mb-4">
        <FileVideo className="w-7 h-7 text-white/50" />
      </div>
      <h3 className="font-display text-2xl md:text-3xl uppercase text-white leading-none">
        Load video {label}
      </h3>
      <p className="text-xs md:text-sm text-white/50 mt-2 max-w-xs text-center leading-relaxed">
        Upload a local file or paste a direct URL / YouTube link to begin
        analysis.
      </p>
      <button
        onClick={onPickFile}
        className="mt-6 inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold uppercase tracking-[0.15em] text-xs rounded-md px-5 py-3 transition-colors active:scale-95"
        data-testid={`load-video-${label}-button`}
      >
        <Upload className="w-4 h-4" />
        Load video
      </button>
      <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-white/40">
        <LinkIcon className="w-3 h-3" />
        File · URL · YouTube
      </div>
    </div>
  );
}

export default VideoPanel;
